// Client Portal Route Configuration
// Routes with pageKey property for permission-based access control
import { lazy } from 'react';

// Lazy load Client Portal components
const ClientDashboardOriginal = lazy(
  () => import('../../portals/client/pages/dashboard/ClientDashboardOriginal')
);
const VehiclesPage = lazy(() => import('../../portals/client/pages/fleet/VehiclesPage'));
const DevicesPage = lazy(() => import('../../portals/client/pages/fleet/DevicesPage'));
const FleetOverview = lazy(() => import('../../portals/client/pages/fleet/FleetOverview'));
const EventsTracking = lazy(() => import('../../portals/client/pages/events/ClientEventsPage'));
const ReportsCenter = lazy(() => import('../../portals/client/pages/reports/ReportsCenter'));
const FleetChat = lazy(() => import('../../portals/client/pages/chat/FleetChat'));
const HealthMonitoring = lazy(() => import('../../portals/client/pages/health/HealthMonitoring'));
const ClientSettings = lazy(() => import('../../portals/client/pages/settings/ClientSettings'));
const HelpSupport = lazy(() => import('../../portals/client/pages/HelpSupport'));
const MySupportPage = lazy(() => import('../../shared/support/pages/MySupportPage'));

/**
 * Client Portal Routes Configuration
 * For end-user clients
 */
export const clientRoutes = {
  path: '/client',
  name: 'Client Portal',
  description: 'Portal for end-user clients',
  requiredRoles: [], // Dynamic RBAC - portal access controlled by assigned_portal
  requiredPermissions: [], // No hardcoded permissions required
  children: [
    {
      index: true,
      component: ClientDashboardOriginal,
      name: 'Dashboard',
      description: 'Client fleet dashboard',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'dashboard', // Permission key for access control
      meta: {
        title: 'Fleet Dashboard',
        breadcrumb: 'Dashboard',
        icon: 'dashboard',
      },
    },
    {
      path: 'vehicles',
      component: VehiclesPage,
      name: 'Vehicles',
      description: 'Manage fleet vehicles',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'vehicles', // Permission key for access control
      meta: {
        title: 'Fleet Vehicles',
        breadcrumb: 'Vehicles',
        icon: 'directions_car',
      },
    },
    {
      path: 'devices',
      component: DevicesPage,
      name: 'Devices',
      description: 'Manage fleet devices',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'devices', // Permission key for access control
      meta: {
        title: 'Fleet Devices',
        breadcrumb: 'Devices',
        icon: 'sensors',
      },
    },
    {
      path: 'drivers',
      component: lazy(() => import('../../portals/client/pages/drivers/DriverManagement')),
      name: 'Drivers',
      description: 'Manage fleet drivers',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'drivers', // Permission key for access control
      meta: {
        title: 'Driver Management',
        breadcrumb: 'Drivers',
        icon: 'person',
      },
    },
    {
      path: 'vehicles-old',
      component: lazy(() => import('../../portals/client/pages/vehicles/VehicleMonitoring')),
      name: 'Vehicle Monitoring Old',
      description: 'Monitor individual vehicles',
      requiredPermissions: [], // No hardcoded permissions required
      meta: {
        title: 'Vehicle Monitoring',
        breadcrumb: 'Vehicles Old',
        icon: 'directions_car',
        hideInNavigation: true,
      },
      children: [
        {
          path: ':vehicleId',
          component: lazy(() => import('../../portals/client/pages/vehicles/VehicleDetail')),
          name: 'Vehicle Detail',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Vehicle Details',
            breadcrumb: 'Vehicle Details',
            hideInNavigation: true,
          },
        },
        {
          path: ':vehicleId/history',
          component: lazy(() => import('../../portals/client/pages/vehicles/VehicleHistory')),
          name: 'Vehicle History',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Vehicle History',
            breadcrumb: 'History',
            hideInNavigation: true,
          },
        },
        {
          path: 'map',
          component: lazy(() => import('../../portals/client/pages/vehicles/VehicleMap')),
          name: 'Vehicle Map',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Vehicle Map',
            breadcrumb: 'Map View',
          },
        },
      ],
    },
    {
      path: 'fleet',
      component: FleetOverview,
      name: 'Fleet Overview',
      description: 'Complete fleet overview and analytics',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'fleet_overview', // Permission key for access control
      meta: {
        title: 'Fleet Overview',
        breadcrumb: 'Fleet',
        icon: 'fleet',
      },
      children: [
        {
          path: 'analytics',
          component: lazy(() => import('../../portals/client/pages/fleet/FleetAnalytics')),
          name: 'Fleet Analytics',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Fleet Analytics',
            breadcrumb: 'Analytics',
          },
        },
        {
          path: 'efficiency',
          component: lazy(() => import('../../portals/client/pages/fleet/FleetEfficiency')),
          name: 'Fleet Efficiency',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Fleet Efficiency',
            breadcrumb: 'Efficiency',
          },
        },
        {
          path: 'maintenance',
          component: lazy(() => import('../../portals/client/pages/fleet/FleetMaintenance')),
          name: 'Fleet Maintenance',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Fleet Maintenance',
            breadcrumb: 'Maintenance',
          },
        },
      ],
    },
    {
      path: 'events',
      component: EventsTracking,
      name: 'Events Tracking',
      description: 'Track and manage fleet events',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'events', // Permission key for access control
      meta: {
        title: 'Events Tracking',
        breadcrumb: 'Events',
        icon: 'event',
      },
      children: [
        {
          path: ':eventId',
          component: lazy(() => import('../../portals/client/pages/events/EventDetail')),
          name: 'Event Detail',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Event Details',
            breadcrumb: 'Event Details',
            hideInNavigation: true,
          },
        },
        {
          path: 'alerts',
          component: lazy(() => import('../../portals/client/pages/events/EventAlerts')),
          name: 'Event Alerts',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Event Alerts',
            breadcrumb: 'Alerts',
          },
        },
        {
          path: 'timeline',
          component: lazy(() => import('../../portals/client/pages/events/EventTimeline')),
          name: 'Event Timeline',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Event Timeline',
            breadcrumb: 'Timeline',
          },
        },
      ],
    },
    {
      path: 'reports',
      component: ReportsCenter,
      name: 'Reports Center',
      description: 'Generate and view fleet reports',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'reports', // Permission key for access control
      meta: {
        title: 'Reports Center',
        breadcrumb: 'Reports',
        icon: 'assessment',
      },
      children: [
        {
          path: 'fleet-summary',
          component: lazy(() => import('../../portals/client/pages/reports/FleetSummaryReport')),
          name: 'Fleet Summary',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Fleet Summary Report',
            breadcrumb: 'Fleet Summary',
          },
        },
        {
          path: 'driver-behavior',
          component: lazy(() => import('../../portals/client/pages/reports/DriverBehaviorReport')),
          name: 'Driver Behavior',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Driver Behavior Report',
            breadcrumb: 'Driver Behavior',
          },
        },
        {
          path: 'fuel-efficiency',
          component: lazy(() => import('../../portals/client/pages/reports/FuelEfficiencyReport')),
          name: 'Fuel Efficiency',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Fuel Efficiency Report',
            breadcrumb: 'Fuel Efficiency',
          },
        },
        {
          path: 'maintenance',
          component: lazy(() => import('../../portals/client/pages/reports/MaintenanceReport')),
          name: 'Maintenance Report',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Maintenance Report',
            breadcrumb: 'Maintenance Report',
          },
        },
        {
          path: 'custom',
          component: lazy(() => import('../../portals/client/pages/reports/CustomReports')),
          name: 'Custom Reports',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Custom Reports',
            breadcrumb: 'Custom Reports',
          },
        },
      ],
    },
    {
      path: 'chat',
      component: FleetChat,
      name: 'FleetChat',
      description: 'AI-powered fleet assistance chat',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'fleet_chat', // Permission key for access control
      meta: {
        title: 'FleetChat',
        breadcrumb: 'Chat',
        icon: 'chat',
      },
    },
    {
      path: 'support',
      component: lazy(() => import('../../portals/client/pages/support/ClientSupportTickets')),
      name: 'Support Tickets',
      description: 'View and manage support tickets',
      requiredPermissions: [],
      pageKey: 'support', // Permission key for access control
      meta: {
        title: 'Support Tickets',
        breadcrumb: 'Support',
        icon: 'support',
      },
    },
    {
      path: 'notifications',
      component: lazy(() => import('../../portals/client/pages/notifications/ClientNotifications')),
      name: 'Notifications',
      description: 'View all notifications',
      requiredPermissions: [],
      pageKey: 'notifications', // Permission key for access control
      meta: {
        title: 'Notifications',
        breadcrumb: 'Notifications',
        icon: 'notifications',
      },
    },
    {
      path: 'health',
      component: HealthMonitoring,
      name: 'Health Monitoring',
      description: 'Monitor fleet and vehicle health',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'health', // Permission key for access control
      meta: {
        title: 'Health Monitoring',
        breadcrumb: 'Health',
        icon: 'health_and_safety',
      },
      children: [
        {
          path: 'fleet-health',
          component: lazy(() => import('../../portals/client/pages/health/FleetHealthOverview')),
          name: 'Fleet Health Overview',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Fleet Health Overview',
            breadcrumb: 'Fleet Health',
          },
        },
        {
          path: 'vehicle-diagnostics',
          component: lazy(() => import('../../portals/client/pages/health/VehicleDiagnostics')),
          name: 'Vehicle Diagnostics',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Vehicle Diagnostics',
            breadcrumb: 'Diagnostics',
          },
        },
        {
          path: 'maintenance-alerts',
          component: lazy(() => import('../../portals/client/pages/health/MaintenanceAlerts')),
          name: 'Maintenance Alerts',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Maintenance Alerts',
            breadcrumb: 'Maintenance Alerts',
          },
        },
      ],
    },
    {
      path: 'datagrid-test',
      component: lazy(() => import('../../portals/client/pages/datagrid/DataGridTest')),
      name: 'DataGrid Test',
      description: 'Test 1771 DataGrid component',
      requiredPermissions: [],
      meta: {
        title: 'DataGrid Test',
        breadcrumb: 'DataGrid Test',
        icon: 'table_chart',
      },
    },
    {
      path: 'settings',
      component: ClientSettings,
      name: 'Settings',
      description: 'Client account and preferences',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'settings', // Permission key for access control
      meta: {
        title: 'Client Settings',
        breadcrumb: 'Settings',
        icon: 'settings',
      },
      children: [
        {
          path: 'profile',
          component: lazy(() => import('../../portals/client/pages/settings/ClientProfile')),
          name: 'Profile Settings',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Profile Settings',
            breadcrumb: 'Profile',
          },
        },
        {
          path: 'notifications',
          component: lazy(() => import('../../portals/client/components/NotificationSettings')),
          name: 'Notification Settings',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'Notification Settings',
            breadcrumb: 'Notifications',
          },
        },
        {
          path: 'preferences',
          component: lazy(() => import('../../portals/client/components/UserPreferences')),
          name: 'User Preferences',
          requiredPermissions: [], // No hardcoded permissions required
          meta: {
            title: 'User Preferences',
            breadcrumb: 'Preferences',
          },
        },
      ],
    },
    {
      path: 'my-support',
      component: MySupportPage,
      name: 'My Support',
      description: 'My tickets and notifications',
      requiredPermissions: [],
      meta: {
        title: 'My Support',
        breadcrumb: 'Support',
        icon: 'support_agent',
      },
    },
    {
      path: 'help',
      component: HelpSupport,
      name: 'Help & Support',
      description: 'Help articles and support resources',
      requiredPermissions: [], // No hardcoded permissions required
      pageKey: 'help', // Permission key for access control
      meta: {
        title: 'Help & Support',
        breadcrumb: 'Help',
        icon: 'help',
      },
    },
  ],
};

export default clientRoutes;
